import { ComponentRegistry } from './componentRegistry.js';

/** Scene-level lifecycle events (source_object_id = scene id). */
export const SCENE_LEVEL_EVENTS = [
  { value: 'roomEntered', labelKey: 'event-room-entered' },
  { value: 'introAnimationStarted', labelKey: 'event-intro-animation-started' },
  { value: 'introAnimationEnded', labelKey: 'event-intro-animation-ended' },
  { value: 'welcomeClosed', labelKey: 'event-welcome-closed' },
  { value: 'consentDeclined', labelKey: 'event-consent-declined' },
];

export const SCENE_SOURCE_TYPE = 'scene';

/** Space-level welcome screen (not a placed scene object). */
export const WELCOME_SOURCE_TYPE = 'welcome';
export const WELCOME_OBJECT_ID = 'welcome';

export function isWelcomeEventTarget(objectId, objectType) {
  return objectType === WELCOME_SOURCE_TYPE || String(objectId) === WELCOME_OBJECT_ID;
}

/**
 * Raycast hover events available to any raycastable scene object.
 * Fired when the camera-center ray enters/leaves the element (see ModeView per-frame raycast).
 */
export const RAYCAST_EVENTS = [
  { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
  { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
];

const RAYCAST_EVENT_VALUES = new Set(RAYCAST_EVENTS.map((e) => e.value));

export function isRaycastEventType(eventType) {
  return RAYCAST_EVENT_VALUES.has(eventType);
}

const SCENE_LEVEL_EVENT_VALUES = new Set(SCENE_LEVEL_EVENTS.map((e) => e.value));

export function isSceneLevelEventType(eventType) {
  return SCENE_LEVEL_EVENT_VALUES.has(eventType);
}

export function getSceneLevelEvents() {
  return SCENE_LEVEL_EVENTS;
}

export function isSceneLevelSource(sourceObjectId, sceneId) {
  if (!sourceObjectId || !sceneId) return false;
  return String(sourceObjectId) === String(sceneId);
}

/**
 * Events each component type can trigger.
 * Used when adding Show/Hide actions: pick trigger (component + event).
 */
export const COMPONENT_EVENTS = {
  webLink: [{ value: 'click', labelKey: 'event-click' }],
  vapiButton: [{ value: 'click', labelKey: 'event-click' }],
  vraiAgent: [
    { value: 'click', labelKey: 'event-click' },
    { value: 'raycastEnter', labelKey: 'event-raycast-enter' },
    { value: 'raycastLeave', labelKey: 'event-raycast-leave' },
    { value: 'callStarted', labelKey: 'event-call-started' },
    { value: 'callEnded', labelKey: 'event-call-ended' },
    { value: 'agentChanged', labelKey: 'event-agent-changed' },
  ],
  imageGallery: [{ value: 'click', labelKey: 'event-click' }],
  videoPlayer: [
    { value: 'click', labelKey: 'event-click' },
    { value: 'videoStartedPlaying', labelKey: 'event-video-started-playing' },
    { value: 'stoppedPlaying', labelKey: 'event-stopped-playing' },
  ],
  videoPlayerWebGL: [
    { value: 'click', labelKey: 'event-click' },
    { value: 'videoStartedPlaying', labelKey: 'event-video-started-playing' },
    { value: 'stoppedPlaying', labelKey: 'event-stopped-playing' },
  ],
  videoGallery: [
    { value: 'click', labelKey: 'event-click' },
    { value: 'videoStartedPlaying', labelKey: 'event-video-started-playing' },
    { value: 'stoppedPlaying', labelKey: 'event-stopped-playing' },
  ],
  vrInvisibleAction: [{ value: 'click', labelKey: 'event-click' }],
  vrMusicObject: [
    { value: 'playOnce', labelKey: 'event-play-once' },
    { value: 'playLoop', labelKey: 'event-play-loop' },
    { value: 'stoppedPlaying', labelKey: 'event-stopped-playing' },
  ],
  iframeElement: [{ value: 'click', labelKey: 'event-click' }],
  timerElement: [
    { value: 'timerStarted', labelKey: 'event-timer-started' },
    { value: 'timerStopped', labelKey: 'event-timer-stopped' },
    { value: 'timerEnded', labelKey: 'event-timer-ended' },
  ],
};
// Add more as components support events: youtube: ['click'], profile: ['click'], etc.

export function getEventsForComponentType(type) {
  // Scene-level sources are not raycastable objects; they keep their own event set.
  if (type === SCENE_SOURCE_TYPE) return SCENE_LEVEL_EVENTS;
  // Welcome is a space-level virtual target/source, not a 3D raycastable object.
  if (type === WELCOME_SOURCE_TYPE) {
    return ComponentRegistry.get(type)?.events ?? [];
  }
  const meta = ComponentRegistry.get(type);
  const base = meta?.events ?? COMPONENT_EVENTS[type] ?? [];
  const merged = [...base];
  const seen = new Set(merged.map((e) => e.value));
  RAYCAST_EVENTS.forEach((eventDef) => {
    if (seen.has(eventDef.value)) return;
    merged.push(eventDef);
    seen.add(eventDef.value);
  });
  return merged;
}

/**
 * Whether the object settings "Events/Actions" tab should be shown for this scene type.
 * True if the type can emit at least one world event and/or can be the target of at least one action
 * (including shared show/hide).
 */
export function sceneObjectTypeSupportsEventActionsTab(type) {
  if (!type || typeof type !== 'string') return false;
  if (getEventsForComponentType(type).length > 0) return true;
  const actions = getActionsForType(type);
  return Array.isArray(actions) && actions.length > 0;
}

/**
 * Actions shared by ALL component types (show/hide visibility).
 * Available in "Bind event to action" for any target component.
 */
export const SHARED_ACTIONS = [
  { value: 'show', labelKey: 'action-show' },
  { value: 'hide', labelKey: 'action-hide' },
  { value: 'animateCameraLookAt', labelKey: 'action-animate-camera-look-at' },
];

/**
 * Actions available per component type for event/action system.
 * When a source object (e.g. webLink) is clicked, we execute actions on target components.
 */
export const COMPONENT_ACTIONS = {
  sceneChange: [
    { value: 'goToScene', labelKey: 'action-go-to-scene' },
  ],
  vraiAgent: [
    { value: 'sendCommand', labelKey: 'action-send-command' },
    { value: 'startCall', labelKey: 'action-start-call' },
    { value: 'endCall', labelKey: 'action-end-call' },
    { value: 'switchAgent', labelKey: 'action-switch-agent' },
    { value: 'openInputField', labelKey: 'action-open-input-field' },
  ],
  vapiUserCamera: [
    { value: 'sendCommand', labelKey: 'action-send-command' },
  ],
  vapiButton: [
    { value: 'sendCommand', labelKey: 'action-send-command' },
  ],
  webLink: [
    { value: 'openLink', labelKey: 'action-open-link' },
  ],
  baglessRoom: [
    { value: 'goToRoom', labelKey: 'action-go-to-room' },
  ],
  youtube: [
    { value: 'play', labelKey: 'action-play' },
    { value: 'pause', labelKey: 'action-pause' },
  ],
  iframeAgent: [
    { value: 'open', labelKey: 'action-open' },
  ],
  iframeElement: [
    { value: 'click', labelKey: 'event-click' },
  ],
  timerElement: [
    { value: 'startTimer', labelKey: 'action-start-timer' },
    { value: 'stopTimer', labelKey: 'action-stop-timer' },
    { value: 'resetTimer', labelKey: 'action-reset-timer' },
  ],
  videoPlayer: [
    { value: 'play', labelKey: 'action-play' },
    { value: 'pause', labelKey: 'action-pause' },
    { value: 'stopPlaying', labelKey: 'action-stop-playing' },
    { value: 'closeVideo', labelKey: 'action-close-video' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  videoPlayerWebGL: [
    { value: 'playOnce', labelKey: 'action-play-once' },
    { value: 'playLoop', labelKey: 'action-play-loop' },
    { value: 'stopPlaying', labelKey: 'action-stop-playing' },
    { value: 'play', labelKey: 'action-play' },
    { value: 'pause', labelKey: 'action-pause' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  vrMusicObject: [
    { value: 'playOnce', labelKey: 'action-play-once' },
    { value: 'playLoop', labelKey: 'action-play-loop' },
    { value: 'stopPlaying', labelKey: 'action-stop-playing' },
  ],
  profile: [
    { value: 'showProfile', labelKey: 'action-show-profile' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  infoElement: [
    { value: 'showInfo', labelKey: 'action-show-info' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  embeddedWebpage: [
    { value: 'openEmbeddedWebpage', labelKey: 'action-open-embedded-webpage' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  imageGallery: [
    { value: 'showGallery', labelKey: 'action-show-gallery' },
    { value: 'startSlideshow', labelKey: 'action-start-slideshow' },
    { value: 'nextImage', labelKey: 'action-next-image' },
    { value: 'previousImage', labelKey: 'action-previous-image' },
    { value: 'closeGallery', labelKey: 'action-close-gallery' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  videoGallery: [
    { value: 'playOnce', labelKey: 'action-play-once' },
    { value: 'playLoop', labelKey: 'action-play-loop' },
    { value: 'stopPlaying', labelKey: 'action-stop-playing' },
    { value: 'openGallery', labelKey: 'action-open-gallery' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  welcome: [
    { value: 'openWelcome', labelKey: 'action-open-welcome' },
    { value: 'closeWelcome', labelKey: 'action-close-welcome' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  inputField: [
    { value: 'openInputField', labelKey: 'action-open-input-field' },
    { value: 'open', labelKey: 'action-open-input-field' },
    { value: 'close', labelKey: 'action-input-field-close' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  inputFieldGuess: [
    { value: 'open', labelKey: 'action-input-guess-open' },
    { value: 'close', labelKey: 'action-input-guess-close' },
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
  [SCENE_SOURCE_TYPE]: [
    { value: 'closeElement', labelKey: 'action-close-element' },
  ],
};

/** Legacy Image Gallery actions still executed if already saved; not offered in the picker. */
export const IMAGE_GALLERY_LEGACY_ACTIONS = [
  { value: 'showImage', labelKey: 'action-show-image' },
  { value: 'openModal', labelKey: 'action-open-modal' },
];

export function getImageGalleryPanelActions() {
  return [...(COMPONENT_ACTIONS.imageGallery || []), ...IMAGE_GALLERY_LEGACY_ACTIONS];
}

/** Legacy music event type saved before rename to stoppedPlaying. */
export function normalizeMusicEventType(eventType) {
  if (eventType === 'stopPlaying') return 'stoppedPlaying';
  return eventType;
}

export function getEventLabelKey(eventType) {
  const normalized = normalizeMusicEventType(eventType);
  const fromScene = SCENE_LEVEL_EVENTS.find((e) => e.value === normalized);
  if (fromScene?.labelKey) return fromScene.labelKey;
  const fromRaycast = RAYCAST_EVENTS.find((e) => e.value === normalized);
  if (fromRaycast?.labelKey) return fromRaycast.labelKey;
  for (const events of Object.values(COMPONENT_EVENTS)) {
    const found = events.find((e) => e.value === normalized);
    if (found?.labelKey) return found.labelKey;
  }
  const meta = ComponentRegistry.get(eventType);
  const fromMeta = meta?.events?.find((e) => e.value === eventType);
  return fromMeta?.labelKey || null;
}

export function getActionsForType(type) {
  const meta = ComponentRegistry.get(type);
  const typeActions = meta?.actions ?? COMPONENT_ACTIONS[type] ?? [];
  // Command Action and welcome screen are non-visual targets; only expose explicit actions.
  if (type === 'vapiButton' || type === WELCOME_SOURCE_TYPE) return [...typeActions];
  const merged = [...SHARED_ACTIONS, ...typeActions];
  const seen = new Set();
  return merged.filter((a) => {
    if (seen.has(a.value)) return false;
    seen.add(a.value);
    return true;
  });
}

/** Get the labelKey for an action value (for display in event lists). */
export function getActionLabelKey(actionValue) {
  const all = [...SHARED_ACTIONS, ...Object.values(COMPONENT_ACTIONS).flat(), ...IMAGE_GALLERY_LEGACY_ACTIONS];
  const found = all.find((a) => a.value === actionValue);
  if (found?.labelKey) return found.labelKey;
  for (const meta of ComponentRegistry.getAll()) {
    const fromMeta = meta?.actions?.find((a) => a.value === actionValue);
    if (fromMeta?.labelKey) return fromMeta.labelKey;
  }
  return null;
}

/**
 * Room-AI outbound events (AI → space), inverse of command buttons.
 * Stored on chair dbConfig.aiEvents as { id, name, whenToCall, label? }.
 * `name` is the Vapi client-tool / worldEvents.event_type slug.
 */
export function slugifyAiEventName(raw) {
  let s = String(raw || '')
    .trim()
    .replace(/[^a-zA-Z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (!s) return '';
  if (/^[0-9]/.test(s)) s = `event_${s}`;
  return s.slice(0, 64);
}

export function normalizeAiEvents(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') continue;
    const name = slugifyAiEventName(item.name || item.eventName);
    if (!name || seen.has(name)) continue;
    seen.add(name);
    const whenToCall = String(item.whenToCall ?? item.when ?? '').trim();
    const label = String(item.label || item.displayName || name).trim() || name;
    out.push({
      id: String(item.id || name),
      name,
      whenToCall,
      label,
    });
  }
  return out;
}

export function getAiEventsFromConfig(cfg) {
  return normalizeAiEvents(cfg?.aiEvents);
}

/** Event defs for Events/Actions UI (merged onto vraiAgent built-ins). */
export function aiEventsToEventDefs(aiEvents) {
  return normalizeAiEvents(aiEvents).map((e) => ({
    value: e.name,
    label: e.label || e.name,
    labelKey: null,
    isAiEvent: true,
  }));
}

export function mergeComponentEventsWithAiEvents(baseEvents, aiEvents) {
  const merged = Array.isArray(baseEvents) ? [...baseEvents] : [];
  const seen = new Set(merged.map((e) => e.value));
  aiEventsToEventDefs(aiEvents).forEach((def) => {
    if (seen.has(def.value)) return;
    merged.push(def);
    seen.add(def.value);
  });
  return merged;
}

/**
 * Vapi OpenAIFunctionParameters.properties must be a map of JSON Schema objects.
 * Empty `{}` and non-object values are rejected:
 * "each value in tools:append.function.parameters.properties must be an object".
 * No-arg tools omit `parameters` entirely (Vapi: empty parameter list).
 */
function normalizeVapiFunctionParameters(parameters) {
  if (!parameters || typeof parameters !== 'object' || Array.isArray(parameters)) return undefined;
  const props = {};
  const rawProps = parameters.properties;
  if (rawProps && typeof rawProps === 'object' && !Array.isArray(rawProps)) {
    Object.entries(rawProps).forEach(([key, schema]) => {
      if (schema && typeof schema === 'object' && !Array.isArray(schema)) {
        props[key] = schema;
      }
    });
  }
  if (!Object.keys(props).length) return undefined;
  const out = { type: 'object', properties: props };
  if (Array.isArray(parameters.required) && parameters.required.length) {
    const required = parameters.required.filter((name) => name in props);
    if (required.length) out.required = required;
  }
  return out;
}

function sanitizeVapiAppendTool(tool) {
  if (!tool || typeof tool !== 'object' || Array.isArray(tool)) return tool;
  const fn = tool.function;
  if (!fn || typeof fn !== 'object' || Array.isArray(fn)) return tool;
  const parameters = normalizeVapiFunctionParameters(fn.parameters);
  const nextFn = { ...fn };
  if (parameters) nextFn.parameters = parameters;
  else delete nextFn.parameters;
  return { ...tool, function: nextFn };
}

/**
 * Client-side function tool (no server URL → browser `tool-calls`).
 * `async: true` so Vapi does not wait for a webhook/result that never arrives.
 */
function buildVapiClientFunctionTool(functionConfig, completeHint) {
  const { parameters, ...rest } = functionConfig || {};
  const functionDef = { ...rest };
  const normalized = normalizeVapiFunctionParameters(parameters);
  if (normalized) functionDef.parameters = normalized;
  return {
    type: 'function',
    async: true,
    function: functionDef,
    messages: [
      {
        type: 'request-complete',
        role: 'system',
        content: completeHint,
      },
    ],
  };
}

/** Vapi client tools (no server URL → delivered as browser `tool-calls`). */
export function buildVapiClientToolsFromAiEvents(aiEvents) {
  return normalizeAiEvents(aiEvents).map((e) => buildVapiClientFunctionTool(
    {
      name: e.name,
      description: e.whenToCall
        || `Trigger the room event "${e.label || e.name}".`,
    },
    'The room action completed successfully. Continue naturally. Do not say the tool failed or was not called.',
  ));
}

export function buildVapiAiEventsSystemPrompt(aiEvents) {
  const events = normalizeAiEvents(aiEvents);
  if (!events.length) return '';
  const lines = events.map((e) => {
    const when = e.whenToCall || 'when appropriate for this room';
    return `- ${e.name}: ${when}`;
  });
  return [
    'Follow your configured assistant role and personality.',
    'Additionally, you can trigger room events by calling these tools.',
    'Only call a tool when its rule applies. Do not invent other tool names.',
    ...lines,
  ].join('\n');
}

/** Vapi assistantOverrides key that appends tools without replacing the dashboard model. */
export const VAPI_TOOLS_APPEND_KEY = 'tools:append';

const VAPI_MODEL_PROVIDERS = new Set([
  'openai', 'azure-openai', 'together-ai', 'anyscale', 'openrouter', 'perplexity-ai',
  'deepinfra', 'custom-llm', 'baseten', 'runpod', 'groq', 'vapi', 'anthropic',
  'anthropic-bedrock', 'anthropic-vertex', 'minimax', 'google', 'xai', 'inflection-ai',
  'cerebras', 'deep-seek', 'mistral',
]);

function collectVapiAppendTools(overrides) {
  if (!overrides) return [];
  const fromAppend = Array.isArray(overrides[VAPI_TOOLS_APPEND_KEY]) ? overrides[VAPI_TOOLS_APPEND_KEY] : [];
  const fromModel = (overrides.model && typeof overrides.model === 'object' && !Array.isArray(overrides.model)
    && Array.isArray(overrides.model.tools))
    ? overrides.model.tools
    : [];
  return [...fromAppend, ...fromModel];
}

/** Keep only a Vapi model object that includes a valid provider. Strings like "gpt-4o" are dropped. */
function normalizeVapiOverrideModel(model) {
  if (!model || typeof model !== 'object' || Array.isArray(model)) return null;
  const provider = String(model.provider || '').trim();
  if (!VAPI_MODEL_PROVIDERS.has(provider)) return null;
  const { tools, ...rest } = model;
  return rest;
}

function omitOverrideKeys(overrides, keys) {
  const out = { ...overrides };
  keys.forEach((key) => { delete out[key]; });
  return out;
}

/**
 * Assistant overrides for vapi.start(assistantId, overrides).
 * Injects client tools via tools:append so the dashboard LLM (provider/model) is preserved.
 * Tool descriptions carry whenToCall. Pass options.provider to also replace model.messages.
 */
export function buildVapiAiEventAssistantOverrides(aiEvents, options = {}) {
  const tools = buildVapiClientToolsFromAiEvents(aiEvents);
  if (!tools.length) return null;
  const clientMessages = Array.isArray(options.clientMessages) && options.clientMessages.length
    ? options.clientMessages
    : ['tool-calls', 'function-call', 'transcript', 'hang', 'speech-update'];
  const overrides = {
    clientMessages,
    [VAPI_TOOLS_APPEND_KEY]: tools,
  };
  const provider = String(options.provider || '').trim();
  if (VAPI_MODEL_PROVIDERS.has(provider)) {
    const stub = buildVapiAiEventsSystemPrompt(aiEvents);
    const basePrompt = String(options.baseSystemPrompt || '').trim();
    const content = [basePrompt, stub].filter(Boolean).join('\n\n');
    overrides.model = {
      provider,
      model: options.model || 'gpt-4o-mini',
      messages: [{ role: 'system', content }],
    };
  }
  return overrides;
}

export const VAPI_ROOM_PROMPT_VARIABLE = 'vapiRoomPrompt';

/** Trimmed room-prompt text from chair config, or empty string. */
export function getVapiRoomPromptFromConfig(config) {
  return String(config?.vapiRoomPrompt || '').trim();
}

/**
 * Extra room context for vapi.start assistantOverrides.
 * Uses variableValues so {{vapiRoomPrompt}} in the dashboard prompt is filled
 * without replacing the assistant model/messages.
 */
export function buildVapiRoomPromptOverrides(vapiRoomPrompt) {
  return buildVapiPromptVariableOverrides(vapiRoomPrompt, '');
}

/** System add-message body so the room prompt is extra context even without a dashboard placeholder. */
export function buildVapiRoomPromptSystemContent(vapiRoomPrompt) {
  const prompt = String(vapiRoomPrompt || '').trim();
  if (!prompt) return null;
  return `Extra room context for this agent:\n${prompt}`;
}

export const VAPI_TOKEN_PROMPT_VARIABLE = 'tokenPrompt';

/** Labeled tokenPrompt block so the model sees the variable name, not just the text. */
export function formatVapiTokenPromptBlock(tokenPrompt) {
  const prompt = String(tokenPrompt || '').trim();
  if (!prompt) return '';
  return `${VAPI_TOKEN_PROMPT_VARIABLE}:\n${prompt}`;
}

/**
 * Room + token prompts for vapi.start assistantOverrides.
 * - tokenPrompt is always sent as {{tokenPrompt}}
 * - a labeled tokenPrompt: block is also appended to {{vapiRoomPrompt}} so the
 *   model sees it even when the dashboard assistant has no {{tokenPrompt}} placeholder
 */
export function buildVapiPromptVariableOverrides(roomPrompt, tokenPrompt) {
  const room = String(roomPrompt || '').trim();
  const token = String(tokenPrompt || '').trim();
  const variableValues = {};
  if (token) {
    variableValues[VAPI_TOKEN_PROMPT_VARIABLE] = token;
  }
  const roomValue = [room, formatVapiTokenPromptBlock(token)].filter(Boolean).join('\n\n');
  if (roomValue) {
    variableValues[VAPI_ROOM_PROMPT_VARIABLE] = roomValue;
  }
  if (!Object.keys(variableValues).length) return null;
  const overrides = { variableValues };
  if (token) {
    overrides[VAPI_TOOLS_APPEND_KEY] = [buildVapiTokenPromptContextTool(token)];
  }
  return overrides;
}

function buildVapiTokenPromptContextTool(tokenPrompt) {
  const block = formatVapiTokenPromptBlock(tokenPrompt);
  return buildVapiClientFunctionTool(
    {
      name: VAPI_TOKEN_PROMPT_VARIABLE,
      description: `Do not call this tool. Instantiation context only. ${block}`,
    },
    'Continue the conversation. Do not mention this tool.',
  );
}

/**
 * AI-token prompt for vapi.start assistantOverrides.
 * Uses variableValues so {{tokenPrompt}} in the dashboard prompt is filled
 * without replacing the assistant model/messages.
 */
export function buildVapiTokenPromptOverrides(tokenPrompt) {
  return buildVapiPromptVariableOverrides('', tokenPrompt);
}

/** System add-message body so VAPI knows this is a token prompt even without a dashboard placeholder. */
export function buildVapiTokenPromptSystemContent(tokenPrompt) {
  const block = formatVapiTokenPromptBlock(tokenPrompt);
  if (!block) return null;
  return `The following is tokenPrompt, a visitor-specific token prompt for this call (distinct from vapiRoomPrompt):\n${block}`;
}

export const VAPI_GUEST_USER_VARIABLE = 'isGuest';

export function isCurrentUserGuest(model) {
  return !!(model?.config?.isGuest || model?.isGuest);
}

/**
 * Guest-user flag for vapi.start assistantOverrides.
 * Uses variableValues so {{isGuest}} in the dashboard prompt is filled
 * without replacing the assistant model/messages.
 */
export function buildVapiGuestUserOverrides(isGuest) {
  if (!isGuest) return null;
  return {
    variableValues: {
      [VAPI_GUEST_USER_VARIABLE]: 'true',
    },
  };
}

/** System add-message body so VAPI knows the caller is a guest. */
export function buildVapiGuestUserSystemContent(isGuest) {
  if (!isGuest) return null;
  return 'The current user is a guest user.';
}

export function mergeVapiAssistantOverrides(base, extra) {
  const sources = [base, extra].filter((s) => s && typeof s === 'object');
  if (!sources.length) return null;
  const clientMessages = [...new Set(sources.flatMap((s) => (
    Array.isArray(s.clientMessages) ? s.clientMessages : []
  )))];
  const toolsAppend = sources.flatMap(collectVapiAppendTools).map(sanitizeVapiAppendTool);
  const variableValues = Object.assign(
    {},
    ...sources.map((s) => (
      s.variableValues && typeof s.variableValues === 'object' && !Array.isArray(s.variableValues)
        ? s.variableValues
        : {}
    )),
  );
  const metadata = Object.assign(
    {},
    ...sources.map((s) => (
      s.metadata && typeof s.metadata === 'object' && !Array.isArray(s.metadata)
        ? s.metadata
        : {}
    )),
  );
  const models = sources.map((s) => normalizeVapiOverrideModel(s.model)).filter(Boolean);
  let model = null;
  if (models.length) {
    const contents = models.map((m) => m.messages?.[0]?.content).filter(Boolean);
    model = Object.assign({}, ...models);
    if (contents.length > 1 && contents[0] !== contents[contents.length - 1]) {
      model.messages = [{ role: 'system', content: contents.join('\n\n') }];
    } else if (models[models.length - 1].messages) {
      model.messages = models[models.length - 1].messages;
    }
    if (!VAPI_MODEL_PROVIDERS.has(String(model.provider || '').trim())) model = null;
  }
  const merged = Object.assign(
    {},
    ...sources.map((s) => omitOverrideKeys(s, [VAPI_TOOLS_APPEND_KEY, 'model', 'clientMessages', 'variableValues', 'metadata'])),
  );
  if (clientMessages.length) merged.clientMessages = clientMessages;
  if (toolsAppend.length) merged[VAPI_TOOLS_APPEND_KEY] = toolsAppend;
  if (Object.keys(variableValues).length) merged.variableValues = variableValues;
  if (Object.keys(metadata).length) merged.metadata = metadata;
  if (model) merged.model = model;
  return Object.keys(merged).length ? merged : null;
}

export const STORE_SESSION_DATA_TOOL_NAME = 'storeSessionData';

export function buildVapiStoreSessionDataTool() {
  return buildVapiClientFunctionTool(
    {
      name: STORE_SESSION_DATA_TOOL_NAME,
      description: 'Store qualified user data or a conversation summary in the private Bagless session. Never repeat the stored value aloud. Call this after the user provided or confirmed the information.',
      parameters: {
        type: 'object',
        properties: {
          field_key: {
            type: 'string',
            description: 'Short identifier such as email, name, company, or qualification_summary',
          },
          value: {
            type: 'string',
            description: 'The data to store privately in Bagless. This is not sent to analytics.',
          },
          funnel_step: {
            type: 'string',
            description: 'Optional funnel step name for analytics, e.g. qualified or email_captured',
          },
        },
        required: ['field_key', 'value'],
      },
    },
    'The data was stored privately. Continue naturally. Never repeat the stored value aloud.',
  );
}

export function buildVapiStoreSessionDataOverrides() {
  return {
    clientMessages: ['tool-calls', 'function-call', 'transcript', 'hang', 'speech-update'],
    [VAPI_TOOLS_APPEND_KEY]: [buildVapiStoreSessionDataTool()],
  };
}

export const CLOSE_ELEMENT_TOOL_NAME = 'closeElement';

export function buildVapiCloseElementTool() {
  return buildVapiClientFunctionTool(
    {
      name: CLOSE_ELEMENT_TOOL_NAME,
      description: 'Close an open overlay such as an info panel, dialogue, gallery, video, input field, or welcome screen. If object_id is provided, close that element; otherwise close currently open content overlays.',
      parameters: {
        type: 'object',
        properties: {
          object_id: {
            type: 'string',
            description: 'Optional scene object id to close. Omit to close currently open content overlays.',
          },
          object_type: {
            type: 'string',
            description: 'Optional object type such as infoElement, embeddedWebpage, imageGallery, videoGallery, videoPlayer, inputField, or welcome.',
          },
        },
      },
    },
    'The overlay was closed. Continue naturally.',
  );
}

export function buildVapiCloseElementOverrides() {
  return {
    clientMessages: ['tool-calls', 'function-call', 'transcript', 'hang', 'speech-update'],
    [VAPI_TOOLS_APPEND_KEY]: [buildVapiCloseElementTool()],
  };
}

const compDefaults = (w, h, active, pos) => ({
  active,
  position: { x: pos.x, y: pos.y, z: pos.z ?? 0 },
  topLeft: { x: -w / 2, y: h / 2, z: 0 },
  topRight: { x: w / 2, y: h / 2, z: 0 },
  bottomRight: { x: w / 2, y: -h / 2, z: 0 },
  bottomLeft: { x: -w / 2, y: -h / 2, z: 0 },
});

const DEFAULT_COMPONENTS = {
  chair: compDefaults(160, 140, true, { x: -120, y: 0, z: 0 }),
  camera: compDefaults(160, 140, false, { x: 120, y: 0, z: 0 }),
  transcription: compDefaults(200, 80, false, { x: 0, y: -140, z: 0 }),
};

function parseLibraryAgentConfig(libraryAgent) {
  if (!libraryAgent?.agent_config) return null;
  try {
    return typeof libraryAgent.agent_config === 'string'
      ? JSON.parse(libraryAgent.agent_config)
      : libraryAgent.agent_config;
  } catch {
    return null;
  }
}

export function buildFullAgentConfigFromLibrary(libraryAgent) {
  const parsed = parseLibraryAgentConfig(libraryAgent);
  const base = {
    VAPI_ASSISTANT_ID: libraryAgent?.vapi_assistant_id || '',
    agent_image: libraryAgent?.agent_image || '',
    chairName: libraryAgent?.name || 'AI Agent',
    chairScenario: parsed?.chairScenario || '',
    components: parsed?.components ? { ...DEFAULT_COMPONENTS, ...parsed.components } : DEFAULT_COMPONENTS,
  };
  if (parsed?.perspectiveOffsets) base.perspectiveOffsets = parsed.perspectiveOffsets;
  // Squad support: propagate squad fields from agent_config into dbConfig
  if (parsed?.isSquad) {
    base.isSquad = true;
    base.VAPI_SQUAD_ID = parsed.VAPI_SQUAD_ID || libraryAgent?.vapi_assistant_id || '';
    if (Array.isArray(parsed.squadMembers)) base.squadMembers = parsed.squadMembers;
    if (parsed.squadCustomVariables != null) base.squadCustomVariables = parsed.squadCustomVariables;
    // Use first member image as chair image if no dedicated agent_image
    if (!base.agent_image && Array.isArray(parsed.squadMembers) && parsed.squadMembers[0]?.image) {
      base.agent_image = parsed.squadMembers[0].image;
    }
  }
  return base;
}

/** Match a placed chair to a library row by vapiLibraryId/libraryId or assistant id. */
export function findLibraryAgentForChair(agents, cfg = {}) {
  if (!agents?.length || !cfg) return null;
  const libId = cfg.vapiLibraryId ?? cfg.libraryId;
  if (libId != null && String(libId) !== '') {
    const byId = agents.find((a) => String(a.id) === String(libId));
    if (byId) return byId;
  }
  const asst = String(cfg.VAPI_ASSISTANT_ID || '').trim();
  if (asst) {
    return agents.find((a) => String(a.vapi_assistant_id || '').trim() === asst) || null;
  }
  return null;
}

/**
 * Merge library agent fields into a placed chair config (UI switch, events, hydration).
 * Preserves per-chair geometry and component activation flags from prevCfg.
 */
ComponentRegistry.register({
  type: SCENE_SOURCE_TYPE,
  displayNameKey: 'scene-event-source',
  events: SCENE_LEVEL_EVENTS,
  actions: COMPONENT_ACTIONS[SCENE_SOURCE_TYPE],
});

ComponentRegistry.register({
  type: WELCOME_SOURCE_TYPE,
  displayNameKey: 'welcome-screen-text',
  actions: COMPONENT_ACTIONS.welcome,
});

export function mergeLibraryAgentIntoChairConfig(prevCfg = {}, libraryAgent, libraryId) {
  const full = buildFullAgentConfigFromLibrary(libraryAgent);
  const prevComponents = prevCfg.components || {};
  const nextComponents = { ...(full.components || {}) };
  if (prevComponents?.camera?.active !== undefined) {
    nextComponents.camera = { ...(nextComponents.camera || {}), active: prevComponents.camera.active };
  }
  if (prevComponents?.transcription?.active !== undefined) {
    nextComponents.transcription = { ...(nextComponents.transcription || {}), active: prevComponents.transcription.active };
  }
  if (prevComponents?.chair?.active !== undefined) {
    nextComponents.chair = { ...(nextComponents.chair || {}), active: prevComponents.chair.active };
  }
  const scenarioFromLib = (full.chairScenario || '').trim();
  const scenarioFromPrev = (prevCfg.chairScenario != null ? String(prevCfg.chairScenario) : '').trim();
  return {
    ...full,
    VAPI_ASSISTANT_ID: libraryAgent?.vapi_assistant_id || full.VAPI_ASSISTANT_ID || '',
    agent_image: libraryAgent?.agent_image || full.agent_image || '',
    chairName: libraryAgent?.name || full.chairName || prevCfg.chairName || 'AI Agent',
    chairScenario: scenarioFromLib || scenarioFromPrev || '',
    vapiLibraryId: libraryId != null ? libraryId : (prevCfg.vapiLibraryId ?? libraryAgent?.id),
    components: nextComponents,
    chairNameSize: prevCfg.chairNameSize ?? full.chairNameSize,
    chairScenarioSize: prevCfg.chairScenarioSize ?? full.chairScenarioSize,
    chairIndicatorSize: prevCfg.chairIndicatorSize ?? full.chairIndicatorSize,
    chairStatusSize: prevCfg.chairStatusSize ?? full.chairStatusSize,
    size: prevCfg.size ?? full.size,
    scale: prevCfg.scale ?? full.scale,
    width: prevCfg.width ?? full.width,
    height: prevCfg.height ?? full.height,
    promptTranscriptModal: prevCfg.promptTranscriptModal ?? full.promptTranscriptModal,
    perspectiveOffsets: prevCfg.perspectiveOffsets ?? full.perspectiveOffsets,
    // Room AI events and room prompt are per placement, not from the library agent.
    aiEvents: Array.isArray(prevCfg.aiEvents) ? prevCfg.aiEvents : (full.aiEvents || []),
    vapiRoomPrompt: prevCfg.vapiRoomPrompt ?? full.vapiRoomPrompt ?? '',
  };
}
